import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldAlert, ArrowRight, CheckCircle2, Linkedin } from "lucide-react";
import { API } from "../api";
import { buildLinkedInAddToProfileUrl } from "../utils/linkedin";

/** Public certificate verification page — reached by scanning the QR code on a
 *  printed certificate. No auth. Returns a redacted, exec-friendly view. */
export default function CertificateVerify() {
  const { certId } = useParams();
  const [state, setState] = useState({ phase: "loading" }); // loading | verified | not_found | revoked | error
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/salescert/verify/${encodeURIComponent(certId)}`)
      .then((r) => {
        setData(r.data);
        setState({ phase: r.data.status === "revoked" ? "revoked" : "verified" });
      })
      .catch((e) => {
        const status = e?.response?.status;
        if (status === 404) setState({ phase: "not_found" });
        else if (status === 410) setState({ phase: "revoked" });
        else {
          setErr(e?.response?.data?.detail || String(e));
          setState({ phase: "error" });
        }
      });
  }, [certId]);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col" data-testid="cert-verify-page">
      {/* Top bar */}
      <div className="border-b border-[#E5E7EB] bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-xs font-heading font-black tracking-tighter">
              COMPASS<span className="text-[#EE2737]"> · </span>
              <span className="text-[#525252] text-[10px] uppercase tracking-widest">by Mercury Solutions · for VI</span>
            </div>
          </div>
          <Link to="/" className="text-xs text-[#525252] hover:text-[#0A0A0A]" data-testid="cert-verify-home">
            Home <ArrowRight size={12} className="inline" />
          </Link>
        </div>
      </div>

      <div className="flex-1 max-w-3xl w-full mx-auto px-6 py-16">
        {state.phase === "loading" && (
          <div className="text-center text-[#878787] py-12" data-testid="cert-verify-loading">
            Looking up certificate <b>{certId}</b>…
          </div>
        )}

        {state.phase === "not_found" && (
          <div className="card p-10 text-center border-2 border-[#EE2737]" data-testid="cert-verify-not-found">
            <ShieldAlert size={40} className="mx-auto text-[#EE2737] mb-4" />
            <h1 className="font-heading font-black text-3xl mb-2">Certificate not found</h1>
            <p className="text-sm text-[#525252]">
              We could not find a certificate matching <code className="bg-[#F5F5F5] px-2 py-1">{certId}</code>.
              Please check the ID or contact Mercury Solutions.
            </p>
          </div>
        )}

        {state.phase === "revoked" && (
          <div className="card p-10 text-center border-2 border-[#B45309]" data-testid="cert-verify-revoked">
            <ShieldAlert size={40} className="mx-auto text-[#B45309] mb-4" />
            <h1 className="font-heading font-black text-3xl mb-2">Certificate revoked</h1>
            <p className="text-sm text-[#525252]">
              This certificate (<b>{certId}</b>) has been revoked. Contact Mercury Solutions for details.
            </p>
          </div>
        )}

        {state.phase === "error" && (
          <div className="card p-10 text-center" data-testid="cert-verify-error">
            <h1 className="font-heading font-black text-3xl mb-2">Verification failed</h1>
            <p className="text-sm text-[#525252]">{err}</p>
          </div>
        )}

        {state.phase === "verified" && data && (
          <div className="space-y-6 fade-in" data-testid="cert-verify-verified">
            <div className="card p-8 border-2 border-[#059669] bg-[#F0FDF4]">
              <div className="flex items-center gap-3 mb-4">
                <ShieldCheck size={28} className="text-[#059669]" />
                <div>
                  <div className="overline text-[#059669]">CERTIFICATE VERIFIED</div>
                  <div className="text-[11px] text-[#065F46]">Issued and unrevoked. Snapshot is immutable.</div>
                </div>
              </div>
              <div className="text-4xl font-heading font-black tracking-tighter" data-testid="cert-verify-name">
                {data.candidate_name}
              </div>
              <p className="text-sm text-[#065F46] mt-2 leading-relaxed">
                Successfully completed all three sections of the{" "}
                <b>{data.program_name}</b> with an aggregate score of{" "}
                <b>{data.marks_awarded} / {data.marks_max}</b>{" "}
                ({Math.round(data.marks_awarded / data.marks_max * 100)}%).
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="card p-5">
                <div className="overline mb-1">KNOWLEDGE MCQ</div>
                <div className="font-heading font-black text-3xl">
                  {data.sections?.mcq ?? "—"} <span className="text-sm text-[#878787] font-normal">/ 50</span>
                </div>
              </div>
              <div className="card p-5">
                <div className="overline mb-1">RECORDED SALES PITCH</div>
                <div className="font-heading font-black text-3xl">
                  {data.sections?.sales_pitch ?? "—"} <span className="text-sm text-[#878787] font-normal">/ 25</span>
                </div>
              </div>
              <div className="card p-5">
                <div className="overline mb-1">AI CUSTOMER ROLE PLAY</div>
                <div className="font-heading font-black text-3xl">
                  {data.sections?.roleplay ?? "—"} <span className="text-sm text-[#878787] font-normal">/ 25</span>
                </div>
              </div>
            </div>

            <div className="card p-5 flex flex-wrap gap-x-8 gap-y-2 text-xs text-[#525252]">
              <div>
                <div className="overline mb-1">CERTIFICATE ID</div>
                <b className="text-[#0A0A0A] font-mono" data-testid="cert-verify-id">{data.cert_id}</b>
              </div>
              <div>
                <div className="overline mb-1">ISSUED ON</div>
                <b className="text-[#0A0A0A]">{new Date(data.issued_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</b>
              </div>
              <div>
                <div className="overline mb-1">STATUS</div>
                <span className="badge badge-success"><CheckCircle2 size={12} /> Active</span>
              </div>
              <div className="ml-auto">
                <a
                  href={buildLinkedInAddToProfileUrl({
                    certId: data.cert_id,
                    verifyUrl: `${window.location.origin}/verify/${data.cert_id}`,
                    issuedAtIso: data.issued_at,
                  })}
                  target="_blank"
                  rel="noreferrer"
                  className="btn text-xs !bg-[#0A66C2] !text-white !border-transparent hover:!opacity-90"
                  data-testid="cert-verify-linkedin"
                >
                  <Linkedin size={12} /> Add to LinkedIn
                </a>
              </div>
            </div>

            <div className="text-[11px] text-[#878787] text-center pt-4">
              Verified against Mercury Solutions certification records.
              For discrepancies, contact{" "}
              <a href="mailto:certifications@mercurysolutions.example" className="text-[#EE2737] underline">
                certifications@mercurysolutions.example
              </a>.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
